import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Tooltip, Button, Label, Input, FormGroup } from "reactstrap";
import { ADMIN_PAYMENT_METHOD, ADMIN_FORM } from "common/components/admin-payment-form-proptypes";
import Loading from "common/components/loading";
import AdminPaymentForm from "common/components/admin-payment-form";
import StripePaymentMethod from "common/stripe/stripe-payment-method";
import CreditCardFields from "./credit-card-fields";
import InvoiceFields from "./invoice-fields";
import "./payment-information-section.styles.scss";

const propTypes = {
  cardInfo: PropTypes.arrayOf(
    PropTypes.shape({
      credit_card_number_last_4: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      credit_card_type: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      last_used: PropTypes.node,
      pretty_expiration: PropTypes.node,
      pretty_info: PropTypes.node
    })
  ),
  images: PropTypes.shape({
    cards: PropTypes.string,
    cards2x: PropTypes.string
  }).isRequired,
  errorData: PropTypes.shape({
    base: PropTypes.node,
    billing_zip: PropTypes.node,
    credit_card_cvv: PropTypes.node,
    credit_card_expiration_month: PropTypes.node,
    credit_card_expiration_year: PropTypes.node,
    credit_card_number: PropTypes.node
  }),
  hideExistingCards: PropTypes.bool,
  updateStateCallback: PropTypes.func.isRequired,
  accountholderName: PropTypes.string.isRequired,
  showClientAdminPaymentWarning: PropTypes.bool.isRequired,
  showInvoicePurchasing: PropTypes.bool.isRequired,
  hideCreditCardPurchasing: PropTypes.bool.isRequired,
  paymentOption: PropTypes.string,
  adminPaymentMethod: ADMIN_PAYMENT_METHOD,
  adminForm: ADMIN_FORM,
  stripeRetailEnabled: PropTypes.bool,
  stripeReady: PropTypes.bool
};

const PaymentInformationSection = ({
  cardInfo = [],
  images,
  errorData = {},
  hideExistingCards = false,
  updateStateCallback,
  accountholderName,
  showClientAdminPaymentWarning,
  showInvoicePurchasing,
  hideCreditCardPurchasing,
  paymentOption = "",
  adminPaymentMethod = null,
  adminForm = {},
  stripeRetailEnabled = false,
  stripeReady = false
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [showStripeLoader, setShowStripeLoader] = useState(false);

  useEffect(() => {
    if (!stripeRetailEnabled || stripeReady) {
      setShowStripeLoader(false);
      return undefined;
    }
    // if Stripe takes longer than 500ms to load, show the loading spinner
    const timer = setTimeout(() => setShowStripeLoader(true), 500);
    return () => clearTimeout(timer);
  }, [stripeRetailEnabled, stripeReady]);

  const toggle = () => {
    setTooltipOpen(!tooltipOpen);
  };

  const handleChange = (event, key) => {
    updateStateCallback({ [key]: event.target.value });
  };

  const availableOptions = [
    !hideCreditCardPurchasing && { value: "credit_card", label: "Credit Card" },
    showInvoicePurchasing && { value: "stripe_invoice", label: "Pay via Invoice" },
    adminPaymentMethod?.isEnabled && { value: "admin", label: "Admin Checkout" }
  ].filter(Boolean);

  const showToggleGroup = availableOptions.length > 1;
  const invoiceOnly =
    showInvoicePurchasing && hideCreditCardPurchasing && !adminPaymentMethod?.isEnabled;

  const showCreditCard = !hideCreditCardPurchasing && paymentOption === "credit_card";
  const showInvoice = showInvoicePurchasing && paymentOption === "stripe_invoice";
  const showAdmin = adminPaymentMethod?.isEnabled && paymentOption === "admin";

  const renderCardFields = () => {
    if (!stripeRetailEnabled) {
      return (
        <CreditCardFields
          cardInfo={cardInfo}
          images={images}
          hideExistingCards={hideExistingCards}
          handleChange={handleChange}
          errorData={errorData}
          showClientAdminPaymentWarning={showClientAdminPaymentWarning}
          accountholderName={accountholderName}
        />
      );
    }

    if (!stripeReady) {
      return <Loading loading={showStripeLoader} size={3} title="Loading secure payment form" />;
    }

    return <StripePaymentMethod />;
  };

  return (
    <div className="purchase-modal__payment-information">
      <hr className="my-3" />
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0 font-weight-bold">Payment Information</h5>
        <div className="text-right">
          <Button color="link" className="p-0" data-animation={false} id="security">
            Secure Server
          </Button>
          <Tooltip placement="bottom" isOpen={tooltipOpen} target="security" toggle={toggle}>
            Adwerx protects your information with industry-leading security and fraud protection
            systems.
          </Tooltip>
        </div>
      </div>
      {invoiceOnly ? (
        <div data-testid="invoice-fields">
          <Input
            type="hidden"
            name="payment_option"
            value="stripe_invoice"
            data-testid="hidden_payment_option_stripe_invoice"
          />
          <InvoiceFields />
        </div>
      ) : (
        <div data-testid={showToggleGroup ? "payment-options-form" : "credit-card-fields-form"}>
          {showToggleGroup && (
            <div className="payment-options">
              <p className="payment-options-text bold">Select method of payment:</p>
              <FormGroup className="form-fields text-secondary">
                {availableOptions.map(option => (
                  <Label key={option.value} check className="d-inline-block mr-5">
                    <Input
                      type="radio"
                      value={option.value}
                      name="payment_option"
                      data-testid={`payment_option_${option.value}`}
                      onChange={event => handleChange(event, "paymentOption")}
                      checked={paymentOption === option.value}
                      required
                    />
                    {option.label}
                  </Label>
                ))}
              </FormGroup>
            </div>
          )}
          {(showCreditCard || !showToggleGroup) && (
            <div
              data-testid="credit-card-fields"
              className={showToggleGroup ? "credit-card-fields" : ""}
            >
              {renderCardFields()}
            </div>
          )}
          {showInvoice && (
            <div data-testid="invoice-fields">
              <InvoiceFields />
            </div>
          )}
          {showAdmin && (
            <AdminPaymentForm
              paymentMethod={adminPaymentMethod}
              adminForm={adminForm}
              onFieldChange={(field, value) => updateStateCallback({ [field]: value })}
              formErrors={{}}
            />
          )}
        </div>
      )}
    </div>
  );
};

PaymentInformationSection.propTypes = propTypes;

export default PaymentInformationSection;
